import { useEffect } from 'react';

/** How long after the last resize tick the view is restored. */
export const RESIZE_SETTLE_MS = 150;

/** The slice of the Mapbox API this needs — kept narrow so it is testable. */
export interface ResizableMap<Bounds> {
  getBounds: () => Bounds | null;
  resize: () => void;
  fitBounds: (bounds: Bounds, options: { animate: boolean }) => void;
}

/**
 * Keeps the map's VIEW across a container resize, not just its zoom number.
 *
 * `resize()` alone preserves centre and zoom, so a taller pane renders the same
 * zoom over more ground and whatever was framed stops filling the pane. That
 * reads as the zoom being lost, and nothing re-frames it — the Unit Builder's
 * `fitBounds` is guarded to run once per unit.
 *
 * The bounds are captured once per resize burst, BEFORE the first `resize()`
 * distorts them, and re-applied once the drag settles. Capturing per tick would
 * feed the fit its own output and drift across a drag. `resize()` still runs on
 * every tick so the canvas tracks the pane while dragging.
 *
 * Restores whatever the user was looking at, not a fixed target — panning away
 * and then dragging the splitter keeps the panned view.
 *
 * @param {HTMLElement | null} element - Container to observe.
 * @param {object | null} map - Map to resize, or null before it mounts.
 * @param {number} settleMs - Quiet period before the view is restored.
 * @return {void}
 */
export function usePreserveViewOnResize<Bounds>(
  element: HTMLElement | null,
  map: ResizableMap<Bounds> | null,
  settleMs: number = RESIZE_SETTLE_MS
): void {
  useEffect(() => {
    if (!element || !map) return undefined;

    let pending: Bounds | null = null;
    let settle: number | null = null;

    const observer = new ResizeObserver(() => {
      if (pending === null) pending = map.getBounds();
      map.resize();
      if (settle !== null) window.clearTimeout(settle);
      settle = window.setTimeout(() => {
        const bounds = pending;
        pending = null;
        settle = null;
        if (bounds) map.fitBounds(bounds, { animate: false });
      }, settleMs);
    });

    observer.observe(element);
    return () => {
      observer.disconnect();
      if (settle !== null) window.clearTimeout(settle);
    };
  }, [element, map, settleMs]);
}
